import { useMutation, useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Waiting } from "../../../../animations";
import { ErrorFallback, Head } from "../../../../components/core";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../contexts/NotifyContext";
import {
  Driver,
  EDIT_DRIVER,
  GET_DRIVER_BY_ID,
  GET_DRIVERS,
} from "../../../../graphql/fleets/settings/drivers";
import Form from "./components/Form";

const DriverUpdate = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { addNotify } = useNotifyContext();
  let { driverId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_DRIVER_BY_ID, {
    variables: {
      id: driverId,
    },
  });
  const driver = useMemo<Driver>(() => data?.fetchDriver, [data]);

  const [updateDriver] = useMutation(EDIT_DRIVER, {
    refetchQueries: [
      {
        query: GET_DRIVERS,
      },
    ],
    awaitRefetchQueries: true,
  });

  const handleSubmit = (
    values: any,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    updateDriver({
      variables: {
        id: driverId,
        name: values.name,
        email: values.email,
        mobile: values.mobile,
        address: values.address,
        profileImageUrl: values.profileImageUrl,
        status: values.status,
      },
    })
      .then(({ data }) => {
        actions.setSubmitting(false);
        if (data?.driverUpdate) {
          addNotify({
            type: NotifyType.SUCCESS,
            title: "Driver updated successfully",
          });
          return navigate(`/fleets/settings/drivers`);
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Driver update failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Driver update failed",
          message: error.message,
        });
      });
  };

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={DriverUpdateResource.name}
        heading={DriverUpdateResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Drivers",
            href: "/fleets/settings/drivers",
          },
          {
            name: DriverUpdateResource.name,
            href: null,
          },
        ]}
      />
      <div className="mx-auto max-w-3xl py-6 sm:py-8">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_update_driver")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_update_driver")}
              </p>
            </div>
          </div>

          {loading || !driver ? (
            <Waiting />
          ) : (
            <Form
              initialValues={{
                name: driver.name || "",
                email: driver.email || "",
                mobile: driver.mobile || "",
                address: driver.address || "",
                profileImageUrl: driver.profileImageUrl || "",
                status: driver.status || 0,
              }}
              onSubmit={handleSubmit}
              actionLabel={t("text_update")}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default DriverUpdate;
export const DriverUpdateResource: ResourceProps = {
  name: "Edit Driver",
  description: "Update an existing location",
  access: ["update-drivers"],
  path: "drivers/:driverId",
};
